import { useRef, useCallback, CSSProperties } from 'react'
import { Typography, styled } from '@mui/material'
import { MessageSender, RawVoiceMessage } from '@weblite/ts-action/message'
import { playAudio, pauseAudio, useAudioState } from '@weblite/audio'
import { PlayIcon } from '$common/icons/play'
import { PauseIcon } from '$common/icons/pause'
import { ActionHandler, ActionHandlerState } from '$common/action-handler'
import { useSeek, useContentFileStatus } from '$common/react-hooks/message'
import { formatDuration } from '$func/time'
import * as audioPlayerService from '$logic/audio-player'
import { useSenderName } from '$logic/message/react-hook'
import { MessageMenuIcon } from '../../components/menu-icon'
import { Surfer } from './surfer'

const Container = styled('div')`
  display: flex;
  position: relative;
  align-items: center;
  /* @noflip */
  padding: 10px 10px 10px 36px;
  box-sizing: border-box;
  overflow: hidden;
`

const ContentContainer = styled('div')`
  flex-grow: 1;
  /* @noflip */
  margin-right: 10px;
  overflow: hidden;
`

const VoiceMetaTypography = styled(Typography)(({ theme }) => ({
  display: 'inline-block',
  fontSize: 11,
  color: theme.palette.gray,
  lineHeight: '12px',
}))

const playStateToIconMap = {
  NOT_PLAYING: PlayIcon,
  PLAYING: PauseIcon,
  PAUSED: PlayIcon,
}

export function Voice({
  attachment,
  statusComp,
  uploadId,
  rootStyle,
  iid,
  roomId,
  sender,
}: {
  attachment: RawVoiceMessage['attachment']
  statusComp: JSX.Element | null
  uploadId: string
  rootStyle?: CSSProperties
  iid: number
  roomId: string
  sender: MessageSender
}) {
  const { duration, fileId, wave } = attachment

  const {
    state,
    percentage,
    blobURL,
    cancelOperation,
    startDownload,
  } = useContentFileStatus({ fileId, uploadId, messageType: 'VOICE' })

  const playState = useAudioState(blobURL)
  const containerRef = useRef<HTMLDivElement>(null)
  const seekContainerRef = useRef<HTMLDivElement>(null)

  const canSeek = state === 'AVAILABLE'
  const seekValue = useSeek({
    src: blobURL,
    canSeek,
    containerRef,
    seekContainerRef,
  })

  const audioDurationText = formatDuration(duration)
  const spentDurationText = formatDuration(duration * seekValue) // in case of playing

  const CustomActionIcon =
    state === 'AVAILABLE' ? playStateToIconMap[playState] : undefined

  const { name } = useSenderName(sender)

  const togglePlay = useCallback(() => {
    if (playState === 'PLAYING') pauseAudio(blobURL)
    else {
      audioPlayerService.setData(roomId, iid)
      playAudio(blobURL, duration / 1000, { name, type: 'VOICE' })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playState, blobURL, duration, name])

  return (
    <>
      <Container ref={containerRef} style={{ width: 280, ...rootStyle }}>
        <ActionHandler
          state={state as ActionHandlerState}
          percentage={percentage}
          onCancelClick={cancelOperation}
          onDownloadClick={startDownload}
          onCustomActionIconClick={togglePlay}
          customActionIconComp={CustomActionIcon && <CustomActionIcon />}
        />

        <ContentContainer>
          <Surfer ref={seekContainerRef} wave={wave} seekValue={seekValue} />

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              overflow: 'hidden',
            }}
          >
            <VoiceMetaTypography>{audioDurationText}</VoiceMetaTypography>

            {(playState === 'PLAYING' || playState === 'PAUSED') && (
              <>
                <VoiceMetaTypography
                  sx={{
                    color: '#c4c4c4',
                    margin: '0 4px',
                  }}
                >
                  /
                </VoiceMetaTypography>
                <VoiceMetaTypography>{spentDurationText}</VoiceMetaTypography>
              </>
            )}
          </div>
        </ContentContainer>

        <MessageMenuIcon iid={iid} roomId={roomId} />
      </Container>
      {statusComp}
    </>
  )
}

Voice.defaultProps = {
  rootStyle: null,
}
